// CreateSubcategory.tsx
import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { addMenuCategoryToMenu } from '../../../../../../../../../../crud/menu_api/menus';
import {
  createCategory,
  addSubCategoryToCategory
} from '../../../../../../../../../../crud/menu_api/categories';
import { getWorkstationsByLocalId } from '../../../../../../../../../../crud/management_api/workstations';
import { getAllMenuCategoryTypes } from '../../../../../../../../../../crud/menu_api/menu_category_types';
import MiniLoadingSpinner from '../../../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './CreateSubcategory.scss';


export interface WorkstationOption {
  id: number;
  name: string;
}

export interface MenuOption {
  id: number;
  name: string;
}

/* _________________Get Function:_________________*/
const getWorkstationsOptions = async (): Promise<WorkstationOption[]> => {
  const userContext = localStorage.getItem('user');
  const localId = userContext != null ? JSON.parse(userContext).local_id : 0;
  const response = await getWorkstationsByLocalId(localId);
  if (response?.data) {
    return response.data.map((ws: any) => ({
      id: ws.workstation_id,
      name: ws.workstation_name,
    }));
  }
  return [];
};

/* ======================= RenderCreateSubcategoryModal ======================= */
interface CreateSubcategoryProps {
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
  menu: any;
  categoryId: any;
  onSubcategoryCreated?: (cat: any) => void;
}

export const CreateSubcategory: React.FC<CreateSubcategoryProps> = ({
  showCreateModal,
  setShowCreateModal,
  menu,
  categoryId,
  onSubcategoryCreated,
}) => {
  const [workstations, setWorkstations] = useState<WorkstationOption[]>([]);
  const [menuCategoryTypes, setMenuCategoryTypes] = useState<any[]>([]);
  const [selectedWorkstationId, setSelectedWorkstationId] = useState<number>(0);
  const [selectedMenuCategoryTypeId, setSelectedMenuCategoryTypeId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (showCreateModal) {
      setIsLoading(true);
      getWorkstationsOptions().then(setWorkstations);
      getAllMenuCategoryTypes()
        .then((resp: any) => setMenuCategoryTypes(resp?.data || []))
        .catch(() => setMenuCategoryTypes([]))
        .finally(() => setIsLoading(false));
    }
  }, [showCreateModal]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const userContext = localStorage.getItem('user');
      const localId = userContext ? JSON.parse(userContext).local_id : 0;
      const w_id = selectedWorkstationId === 0 ? null : selectedWorkstationId;
      const menuCategoryTypeId = selectedMenuCategoryTypeId;

      const payload = {
        category_id: 0,
        category_name: (e.currentTarget as any).category_name.value,
        is_main: false,
        workstation_id: w_id,
        local_id: localId,
        state: true,
        menu_category_type_id: menuCategoryTypeId,
      };

      // 1. Create the category
      const createResp = await createCategory(payload);
      toast.success('Categoría creada con éxito!');
      setShowCreateModal(false);

      const created = (createResp as any)?.data;
      if (!created?.category_id || !menu?.menu_id) {
        console.warn('Falta menu_id o category_id al crear categoría');
        return;
      }

      // 2. Associate with menu
      await addMenuCategoryToMenu(menu.menu_id, created.category_id);

      // 3. Associate as subcategory
      if (categoryId?.id) {
        await addSubCategoryToCategory(categoryId.id, created.category_id);
        toast.success('Subcategoría asociada con éxito a la categoría!');
        onSubcategoryCreated?.(created);
        setTimeout(() => window.location.reload(), 200);
      } else {
        console.warn('Falta categoryId al asociar subcategoría');
      }
    } catch (error) {
      console.error('Error inesperado:', error);
      toast.error('Error inesperado al crear o asociar subcategoría');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={showCreateModal}
      onHide={() => setShowCreateModal(false)}
      centered
      className="create-subcategory-modal"
    >
      <Form onSubmit={handleFormSubmit}>
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">
              Crear Subcategoría
            </Modal.Title>
          </div>
          <Button
            variant="none"
            className="modal-close-button"
            onClick={() => setShowCreateModal(false)}
          >
            <X size={24} />
          </Button>
        </Modal.Header>

        {isLoading && (
          <div className="loading-overlay">
            <MiniLoadingSpinner />
          </div>
        )}

        {!isLoading && (
          <Modal.Body>
            <div className="form-container">
              <Form.Group
                controlId="category_name"
                className="mp-formgroup horizontal"
              >
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  name="category_name"
                  placeholder="Ingresa el nombre de la subcategoría"
                  required
                />
              </Form.Group>

              {/* Workstation Dropdown */}
              <Form.Group className="mp-formgroup horizontal">
                <Form.Label>Estación de trabajo</Form.Label>
                <Dropdown
                  onSelect={(key: string | null) =>
                    setSelectedWorkstationId(key ? Number(key) : 0)
                  }
                >
                  <Dropdown.Toggle
                    variant="light"
                    id="dropdown-workstation"
                    className="dropdown-toggle"
                  >
                    {selectedWorkstationId === 0
                      ? 'Seleccionar estación'
                      : workstations.find(w => w.id === selectedWorkstationId)
                          ?.name}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item eventKey="0">-- Ninguna --</Dropdown.Item>
                    {workstations.map(ws => (
                      <Dropdown.Item
                        key={ws.id}
                        eventKey={ws.id.toString()}
                      >
                        {ws.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>

              {/* Category Type Dropdown */}
              <Form.Group className="mp-formgroup horizontal">
                <Form.Label>Tipo de categoría</Form.Label>
                <Dropdown
                  onSelect={(key: string | null) =>
                    setSelectedMenuCategoryTypeId(key ? Number(key) : 0)
                  }
                >
                  <Dropdown.Toggle
                    variant="light"
                    id="dropdown-category-type"
                    className="dropdown-toggle"
                  >
                    {selectedMenuCategoryTypeId === 0
                      ? 'Seleccionar tipo'
                      : menuCategoryTypes.find(
                          (t: any) =>
                            t.menu_category_type_id ===
                            selectedMenuCategoryTypeId
                        )?.name}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item eventKey="0">
                      -- Seleccionar tipo --
                    </Dropdown.Item>
                    {menuCategoryTypes.map((type: any) => (
                      <Dropdown.Item
                        key={type.menu_category_type_id}
                        eventKey={type.menu_category_type_id.toString()}
                      >
                        {type.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </div>
          </Modal.Body>
        )}

        <Modal.Footer>
          <Button
            variant="none"
            onClick={() => setShowCreateModal(false)}
            className="form-button-cancel"
          >
            Cancelar
          </Button>
          <Button
            variant="none"
            type="submit"
            className="form-button-create"
            disabled={isLoading || !selectedMenuCategoryTypeId}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />{' '}
                Creando...
              </>
            ) : (
              'Crear'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateSubcategory;
